// Shared tour detail page shell. Used by both `/tour/$slug` and
// `/$lang/tour/$slug` routes. Kept out of `src/routes/` so the route
// code-splitter does not transform its exports.
import { cleanTourTitle } from "@/lib/presentation-text";
import type { VerifiedDisplayPrice } from "@/lib/presentation-text";
import { PresentationPriceContext } from "@/components/site/FinalTourPrice";
import { categoryLabel } from "@/lib/category-label";
import { CancellationPolicyBlock } from "./CancellationPolicyBlock";
import { Link, useRouterState } from "@tanstack/react-router";
import { resolveTourV2 } from "@/lib/tour-v2-flag";
import { TourV2Layout } from "@/components/tour/v2/TourV2Layout";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Clock,
  MapPin,
  Check,
  Users,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Info,
  Sparkles,
  AlertTriangle,
  Building2,
  ExternalLink,
  ArrowLeft,
} from "lucide-react";

import { getUnifiedTour } from "@/lib/catalog.functions";
import { SiteHeader } from "@/components/site/SiteHeader";
import { SiteFooter } from "@/components/site/SiteFooter";
import { BokunOfficialWidget } from "@/components/booking/BokunOfficialWidget";
import { BokunPricingTable } from "@/components/booking/BokunPricingTable";
import { CustomTourBookingPanel } from "@/components/tour/CustomTourBookingPanel";
import { DestinationDiscovery } from "@/components/tour/DestinationDiscovery";
import { FreeDestinationGuideDownload } from "@/components/tour/FreeDestinationGuideDownload";
import { MayaAudioGuide } from "@/components/maya/MayaAudioGuide";
import { MAYA_PREMIUM_ENABLED } from "@/lib/maya-feature-flag";
import { ReviewsSection } from "@/components/tour/ReviewsSection";
import { ShareButton } from "@/components/site/ShareButton";
import { formatDuration, stripHtml } from "@/lib/slug";
import { normalizeLang } from "@/i18n";
import { TourDesktopLayout } from "@/components/tour/TourDesktopLayout";
import { useTrackTourView } from "@/hooks/use-recovery-intent";
import { VideoLibrary } from "@/components/video/VideoLibrary";

export const tourQuery = (source: "bokun" | "custom", key: string, lang: string) =>
  queryOptions({
    queryKey: ["catalog", "tour", source, key, lang],
    queryFn: () => getUnifiedTour({ data: { source, idOrSlug: key, lang } }),
    staleTime: 5 * 60 * 1000,
  });

export interface TourDetailShellProps {
  source: "bokun" | "custom";
  idKey: string;
  slug: string;
  languageOverride?: string;
  presentationPrices?: Record<string, VerifiedDisplayPrice | null>;
}

export function TourDetailShell(props: TourDetailShellProps) {
  return (
    <PresentationPriceContext.Provider value={props.presentationPrices ?? {}}><div className="min-h-screen bg-background">
      <SiteHeader backHome />

      <Suspense fallback={<div className="h-screen" />}>
        <Body {...props} />
        {props.source === 'bokun' && <div className="mm-container py-8"><VideoLibrary mode="tour" productId={props.idKey} /></div>}
      </Suspense>
      <SiteFooter />
    </div></PresentationPriceContext.Provider>
  );
}

export function TourNotFoundView() {
  const { t } = useTranslation();
  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <div className="mx-auto max-w-2xl px-4 py-32 text-center">
        <h1 className="font-display text-4xl">{t("tour.notFound")}</h1>
        <p className="mt-2 text-muted-foreground">{t("tour.notFoundDesc")}</p>
        <Link
          to="/tours"
          className="mt-6 inline-flex rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground"
        >
          {t("tour.seeAll")}
        </Link>
      </div>
      <SiteFooter />
    </div>
  );
}

function Body({ source, idKey, slug, languageOverride }: TourDetailShellProps) {
  const { t, i18n } = useTranslation();
  const lang = normalizeLang(languageOverride ?? i18n.language);
  const { data } = useSuspenseQuery(tourQuery(source, idKey, lang));
  const searchV2 = useRouterState({
    select: (s) => {
      const v = (s.location.search as Record<string, unknown> | undefined)?.v2;
      return v == null ? null : String(v);
    },
  });

  const p = data.tour ? { ...data.tour, title: cleanTourTitle(data.tour.title) } : null;
  if (!p) return <TourNotFoundView />;
  // Señal de embudo para la recuperación de ventas de Maya (solo registro).
  useTrackTourView({
    tourSlug: slug,
    tourTitle: p.title,
    source: p.source as "bokun" | "custom",
    lang,
  });
  const dur = p.durationText ?? formatDuration(p.durationMinutes);
  const summary = stripHtml(p.summary ?? "");
  const desc = stripHtml(p.description ?? "");

  const isBokun = p.source === "bokun";

  const meetingTypes = t("tour.meetingTypes", { returnObjects: true }) as Record<string, string>;
  const difficulties = t("tour.difficulties", { returnObjects: true }) as Record<string, string>;

  // Plantilla V2 (solo tours habilitados + vista previa o `?v2=1`).
  if (resolveTourV2({ slug, searchV2 })) {
    return (
      <TourV2Layout
        tour={p}
        slug={slug}
        lang={lang}
        summary={summary}
        desc={desc}
        noindex={searchV2 != null}
        previewDateSelector={searchV2 === "1"}
      />
    );
  }

  return (
    <>
      <div className="hidden lg:block">
        <TourDesktopLayout tour={p} slug={slug} lang={lang} summary={summary} desc={desc} />
      </div>
      <div className="tour-typography lg:hidden">
        <div className="mx-auto max-w-7xl pt-2 sm:px-6 sm:pt-4 lg:px-8">
          <MobileGallery images={p.images} title={p.title} />

          <div className="hidden grid-cols-4 gap-2 overflow-hidden rounded-3xl sm:grid sm:h-[440px] lg:h-[540px] lg:gap-3 px-4 sm:px-0">
            {p.images.slice(0, 5).map((img, i) => (
              <div
                key={i}
                className={`relative overflow-hidden bg-muted ${
                  i === 0 ? "col-span-2 row-span-2" : ""
                }`}
              >
                <img
                  src={img}
                  alt={`${p.title} – foto ${i + 1}`}
                  loading={i === 0 ? "eager" : "lazy"}
                  className="h-full w-full object-cover transition-transform duration-700 hover:scale-105"
                />
              </div>
            ))}
          </div>
        </div>

        <div className="mx-auto grid max-w-7xl gap-10 px-4 pb-10 pt-6 sm:px-6 sm:pt-10 lg:grid-cols-[minmax(0,1fr)_400px] lg:gap-14 lg:px-8 lg:pb-14 xl:grid-cols-[minmax(0,1fr)_420px] xl:gap-16">
          <div className="min-w-0 lg:max-w-[760px]">
            <div className="flex flex-wrap items-center gap-2 text-[0.7rem] uppercase tracking-[0.08em]">
              {p.source === "custom" && (
                <span className="rounded-full bg-primary px-3 py-1 font-semibold text-primary-foreground">
                  Mostrando México
                </span>
              )}
              {p.categories.slice(0, 3).map((c) => (
                <span
                  key={c}
                  className="rounded-full bg-accent px-3 py-1 font-semibold text-accent-foreground"
                >
                  {categoryLabel(c)}
                </span>
              ))}
              {p.difficultyLevel && (
                <span className="rounded-full border border-border px-3 py-1 font-medium text-muted-foreground">
                  {t("tour.difficulty")}: {difficulties[p.difficultyLevel] ?? p.difficultyLevel}
                </span>
              )}
              {p.minAge !== null && p.minAge > 0 && (
                <span className="rounded-full border border-border px-3 py-1 font-medium text-muted-foreground">
                  {t("tour.minAge")} {p.minAge}+
                </span>
              )}
            </div>

            <div className="mt-4 flex flex-wrap items-start justify-between gap-3">
              <h1 className="tour-display text-[2rem] leading-[1.05] sm:text-[2.75rem] lg:text-[3.25rem]">
                {p.title}
              </h1>
              <div className="hidden items-center gap-2 md:inline-flex">
                <Link
                  to="/"
                  aria-label={t("nav.backHome", "Volver al inicio")}
                  title={t("nav.backHome", "Volver al inicio")}
                  className="inline-flex items-center gap-2 rounded-full border border-border bg-background px-4 py-2 text-sm font-semibold text-foreground transition-colors hover:bg-accent"
                >
                  <ArrowLeft className="h-4 w-4" />
                  {t("nav.backHome", "Volver al inicio")}
                </Link>
                <ShareButton
                  url={`/tour/${slug}`}
                  title={p.title}
                  text={summary || desc.slice(0, 120)}
                />
              </div>
              <div className="md:hidden">
                <ShareButton
                  url={`/tour/${slug}`}
                  title={p.title}
                  text={summary || desc.slice(0, 120)}
                />
              </div>
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-muted-foreground">
              {p.location && (
                <span className="inline-flex items-center gap-1.5">
                  <MapPin className="h-4 w-4" /> {p.location}
                </span>
              )}
              {dur && (
                <span className="inline-flex items-center gap-1.5">
                  <Clock className="h-4 w-4" /> {dur}
                </span>
              )}
              {p.meetingType && (
                <span className="inline-flex items-center gap-1.5">
                  <Users className="h-4 w-4" />
                  {meetingTypes[p.meetingType] ?? p.meetingType}
                </span>
              )}
            </div>

            {summary && (
              <p className="tour-lead mt-7 text-[1.25rem] leading-[1.55] text-foreground/90 sm:text-[1.375rem]">
                {summary}
              </p>
            )}
            {desc && <ExpandableDescription text={desc} />}

            {p.highlights.length > 0 && (
              <section className="mt-10 rounded-3xl border border-border/70 bg-card p-6 shadow-[0_1px_2px_rgba(0,0,0,0.04)] sm:p-8">
                <h2 className="tour-display inline-flex items-center gap-2 text-2xl">
                  <Sparkles className="h-5 w-5 text-primary" /> {t("tour.highlights")}
                </h2>
                <ul className="mt-5 grid gap-3.5 sm:grid-cols-2">
                  {p.highlights.map((h, i) => (
                    <li
                      key={i}
                      className="flex items-start gap-2.5 text-[0.95rem] leading-[1.6] text-foreground/85"
                    >
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-success" />
                      {stripHtml(h)}
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {p.itinerary.length > 0 && (
              <section className="mt-6 rounded-3xl border border-border/70 bg-card p-6 shadow-[0_1px_2px_rgba(0,0,0,0.04)] sm:p-8">
                <h2 className="tour-display text-2xl">{t("tour.itinerary")}</h2>
                <ol className="mt-5 space-y-6">
                  {p.itinerary.map((it, i) => (
                    <li key={i} className="relative pl-11">
                      <span className="absolute left-0 top-0 inline-flex h-8 w-8 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground">
                        {i + 1}
                      </span>
                      <h3
                        className="text-base font-semibold tracking-tight"
                        style={{ fontFamily: '"Manrope","Inter",sans-serif' }}
                      >
                        {it.title}
                      </h3>
                      {it.body && (
                        <p className="mt-1.5 text-[0.95rem] leading-[1.7] text-foreground/80">
                          {stripHtml(it.body)}
                        </p>
                      )}
                    </li>
                  ))}
                </ol>
              </section>
            )}

            {(p.included.length > 0 || p.excluded.length > 0) && (
              <section className="mt-6 grid gap-6 sm:grid-cols-2">
                {p.included.length > 0 && (
                  <div className="rounded-3xl border border-border/70 bg-card p-6 shadow-[0_1px_2px_rgba(0,0,0,0.04)] sm:p-7">
                    <h3 className="tour-display text-lg">{t("tour.includes")}</h3>
                    <ul className="mt-4 space-y-2.5 text-[0.95rem] leading-[1.6]">
                      {p.included.map((x, i) => (
                        <li key={i} className="flex gap-2.5">
                          <Check className="mt-0.5 h-4 w-4 shrink-0 text-success" /> {stripHtml(x)}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {p.excluded.length > 0 && (
                  <div className="rounded-3xl border border-border/70 bg-card p-6 shadow-[0_1px_2px_rgba(0,0,0,0.04)] sm:p-7">
                    <h3 className="tour-display text-lg">{t("tour.excludes")}</h3>
                    <ul className="mt-4 space-y-2.5 text-[0.95rem] leading-[1.6] text-muted-foreground">
                      {p.excluded.map((x, i) => (
                        <li key={i}>· {stripHtml(x)}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </section>
            )}

            {(p.requirements ||
              p.importantInfo ||
              p.restrictions ||
              p.childrenNotes ||
              p.groupNotes) && (
              <section className="mt-6 rounded-3xl border border-border/70 bg-card p-6 shadow-[0_1px_2px_rgba(0,0,0,0.04)] sm:p-8">
                <h2 className="tour-display inline-flex items-center gap-2 text-2xl">
                  <Info className="h-5 w-5 text-primary" /> {t("tour.importantInfo")}
                </h2>
                {p.requirements && (
                  <div className="mt-5">
                    <h3 className="text-[0.72rem] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                      {t("tour.requirements")}
                    </h3>
                    <p className="mt-2 text-[0.95rem] leading-[1.7] text-foreground/85 whitespace-pre-line">
                      {stripHtml(p.requirements)}
                    </p>
                  </div>
                )}
                {p.importantInfo && (
                  <div className="mt-5">
                    <h3 className="text-[0.72rem] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                      {t("tour.beforeBooking")}
                    </h3>
                    <p className="mt-2 text-[0.95rem] leading-[1.7] text-foreground/85 whitespace-pre-line">
                      {stripHtml(p.importantInfo)}
                    </p>
                  </div>
                )}
                {[p.restrictions, p.childrenNotes, p.groupNotes].filter(Boolean).map((txt, i) => (
                  <p
                    key={i}
                    className="mt-5 text-[0.95rem] leading-[1.7] text-foreground/85 whitespace-pre-line"
                  >
                    {stripHtml(txt as string)}
                  </p>
                ))}
              </section>
            )}

            {p.startPoints.length > 0 && (
              <section className="mt-6 rounded-3xl border border-border/70 bg-card p-6 shadow-[0_1px_2px_rgba(0,0,0,0.04)] sm:p-8">
                <h2 className="tour-display inline-flex items-center gap-2 text-2xl">
                  <MapPin className="h-5 w-5 text-primary" /> {t("tour.meetingPoint")}
                </h2>
                <ul className="mt-5 space-y-4">
                  {p.startPoints.map((sp) => (
                    <li key={sp.id} className="text-[0.95rem]">
                      <div
                        className="font-semibold"
                        style={{ fontFamily: '"Manrope","Inter",sans-serif' }}
                      >
                        {sp.title}
                      </div>
                      {(sp.address || sp.city) && (
                        <div className="mt-0.5 text-muted-foreground">
                          {[sp.address, sp.city].filter(Boolean).join(", ")}
                        </div>
                      )}
                      {sp.latitude && sp.longitude && (
                        <a
                          href={`https://maps.google.com/?q=${sp.latitude},${sp.longitude}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="mt-1.5 inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline"
                        >
                          {t("common.seeOnMaps")} <ExternalLink className="h-3 w-3" />
                        </a>
                      )}
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {p.source === "bokun" ? <CancellationPolicyBlock productId={p.bokunActivityId} className="mt-6" /> : (p.cancellationPolicy || p.changePolicy || p.refundPolicy || p.terms) && (
              <section className="mt-6 rounded-3xl border border-border/70 bg-card p-6 shadow-[0_1px_2px_rgba(0,0,0,0.04)] sm:p-7">
                <h3 className="tour-display inline-flex items-center gap-2 text-lg">
                  <AlertTriangle className="h-5 w-5 text-primary" /> {t("tour.cancellation")}
                </h3>
                {[p.cancellationPolicy, p.changePolicy, p.refundPolicy, p.terms]
                  .filter(Boolean)
                  .map((txt, i) => (
                    <p
                      key={i}
                      className="mt-2.5 text-[0.95rem] leading-[1.7] text-foreground/85 whitespace-pre-line"
                    >
                      {stripHtml(txt as string)}
                    </p>
                  ))}
              </section>
            )}

          </div>

          <aside className="lg:relative">
            {isBokun && p.bokunActivityId ? (
              <div className="space-y-4">
                <BokunPricingTable productId={p.bokunActivityId} />
                <BokunOfficialWidget productId={p.bokunActivityId} lang={lang} />
              </div>
            ) : (
              <CustomTourBookingPanel
                tourId={p.id.replace(/^mx-/, "")}
                fallbackPriceFrom={p.priceFrom ?? null}
                fallbackCurrency={p.currency ?? "MXN"}
                extras={p.bookingExtras ?? []}
                questions={p.bookingQuestions ?? []}
                meetingPoints={p.meetingPoints ?? []}
                minParticipants={p.minBookable ?? null}
                maxParticipants={p.maxBookable ?? null}
                bookingCutoffHours={p.bookingCutoffHours ?? null}
                requiresHotelInfo={!!p.requiresHotelInfo}
                requiresFlightInfo={!!p.requiresFlightInfo}
                hasTerms={!!p.terms}
              />
            )}
          </aside>
        </div>

        {/* Guía gratuita → Descubre este destino → Maya IA → Reseñas */}
        <div className="mx-auto max-w-7xl px-4 pb-16 sm:px-6 lg:px-8">
          <div className="lg:max-w-[760px]">
            <FreeDestinationGuideDownload slug={slug} destinationName={p.location || p.title} />

            <DestinationDiscovery title={p.title} location={p.location} summary={p.summary} itinerary={p.itinerary} highlights={p.highlights} meetingPoints={p.startPoints.map((m) => m.title)} />

            {MAYA_PREMIUM_ENABLED && (
              <div className="mt-8">
                <MayaAudioGuide
                  title={p.title}
                  location={p.location}
                  summary={summary || (desc ? desc.slice(0, 240) : null)}
                  tourSlug={slug}
                  itinerary={p.itinerary}
                  variant="premium"
                />
              </div>
            )}

            <ReviewsSection slug={slug} source={p.source} tourTitle={p.title} />
          </div>
        </div>
      </div>
    </>
  );
}

/**
 * ExpandableDescription — pure UI wrapper for the long description.
 * Shows the first 3 paragraphs by default; the full text stays in the DOM
 * (SEO-safe) but is visually collapsed until the user expands it.
 */
function ExpandableDescription({ text }: { text: string }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const paragraphs = useMemo(() => text.split(/\n+/).slice(0, 20).filter(Boolean), [text]);
  const PREVIEW = 3;
  const preview = paragraphs.slice(0, PREVIEW);
  const rest = paragraphs.slice(PREVIEW);
  const hasMore = rest.length > 0;

  return (
    <div className="tour-prose mt-7 text-foreground/85">
      {preview.map((para, i) => (
        <p key={`p-${i}`}>{para}</p>
      ))}
      {hasMore && (
        <>
          <div
            className={`grid transition-[grid-template-rows,opacity] duration-500 ease-out ${
              expanded ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
            }`}
            aria-hidden={!expanded}
          >
            <div className="min-h-0 overflow-hidden">
              <div className="pt-4">
                {rest.map((para, i) => (
                  <p key={`r-${i}`}>{para}</p>
                ))}
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="group mt-5 inline-flex items-center gap-1.5 rounded-full border border-border bg-background px-5 py-2.5 text-sm font-semibold text-foreground transition hover:border-primary/40 hover:bg-accent"
            aria-expanded={expanded}
          >
            {expanded
              ? t("tour.showLess", { defaultValue: "Mostrar menos" })
              : t("tour.readMore", { defaultValue: "Leer más" })}
            <ChevronDown
              className={`h-4 w-4 transition-transform duration-300 ${expanded ? "rotate-180" : ""}`}
              aria-hidden
            />
          </button>
        </>
      )}
    </div>
  );
}

function MobileGallery({ images, title }: { images: string[]; title: string }) {
  const { t } = useTranslation();
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const [active, setActive] = useState(0);
  const total = images.length;

  const scrollToIndex = useCallback(
    (i: number) => {
      const sc = scrollerRef.current;
      if (!sc) return;
      const clamped = Math.max(0, Math.min(total - 1, i));
      const child = sc.children[clamped] as HTMLElement | undefined;
      if (child) sc.scrollTo({ left: child.offsetLeft - sc.offsetLeft, behavior: "smooth" });
    },
    [total],
  );

  useEffect(() => {
    const sc = scrollerRef.current;
    if (!sc) return;
    const onScroll = () => {
      const center = sc.scrollLeft + sc.clientWidth / 2;
      let best = 0;
      let bestDist = Infinity;
      Array.from(sc.children).forEach((el, i) => {
        const c =
          (el as HTMLElement).offsetLeft + (el as HTMLElement).offsetWidth / 2 - sc.offsetLeft;
        const d = Math.abs(c - center);
        if (d < bestDist) {
          bestDist = d;
          best = i;
        }
      });
      setActive(best);
    };
    sc.addEventListener("scroll", onScroll, { passive: true });
    return () => sc.removeEventListener("scroll", onScroll);
  }, []);

  if (total === 0) return null;

  const prevLabel = t("common.previous", { defaultValue: "Anterior" }) as string;
  const nextLabel = t("common.next", { defaultValue: "Siguiente" }) as string;

  return (
    <section
      className="sm:hidden"
      aria-roledescription="carousel"
      aria-label={
        t("tour.galleryLabel", { defaultValue: "Galería de imágenes del tour" }) as string
      }
      onKeyDown={(e) => {
        if (e.key === "ArrowRight") {
          e.preventDefault();
          scrollToIndex(active + 1);
        } else if (e.key === "ArrowLeft") {
          e.preventDefault();
          scrollToIndex(active - 1);
        }
      }}
    >
      <div className="relative px-4">
        <div className="relative overflow-hidden rounded-2xl bg-muted">
          <div
            ref={scrollerRef}
            className="flex aspect-[4/3] snap-x snap-mandatory overflow-x-auto scroll-smooth [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            tabIndex={0}
            role="group"
            aria-label={
              t("tour.galleryLabel", { defaultValue: "Galería de imágenes del tour" }) as string
            }
          >
            {images.map((img, i) => (
              <div
                key={i}
                role="group"
                aria-roledescription="slide"
                aria-label={`${i + 1} / ${total}`}
                aria-current={i === active ? "true" : undefined}
                className="relative h-full w-full flex-none snap-center bg-muted"
              >
                <img
                  src={img}
                  alt={`${title} – foto ${i + 1}`}
                  loading={i === 0 ? "eager" : "lazy"}
                  decoding={i === 0 ? "sync" : "async"}
                  className="h-full w-full object-cover"
                />
              </div>
            ))}
          </div>

          {total > 1 && (
            <>
              <button
                type="button"
                onClick={() => scrollToIndex(active - 1)}
                disabled={active === 0}
                aria-label={prevLabel}
                className="absolute left-2 top-1/2 -translate-y-1/2 inline-flex h-9 w-9 items-center justify-center rounded-full bg-white/85 text-foreground shadow-md ring-1 ring-black/5 backdrop-blur transition hover:bg-white disabled:opacity-0 disabled:pointer-events-none"
              >
                <ChevronLeft className="h-4 w-4" aria-hidden="true" />
              </button>
              <button
                type="button"
                onClick={() => scrollToIndex(active + 1)}
                disabled={active === total - 1}
                aria-label={nextLabel}
                className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex h-9 w-9 items-center justify-center rounded-full bg-white/85 text-foreground shadow-md ring-1 ring-black/5 backdrop-blur transition hover:bg-white disabled:opacity-0 disabled:pointer-events-none"
              >
                <ChevronRight className="h-4 w-4" aria-hidden="true" />
              </button>

              <div
                className="pointer-events-none absolute right-2 top-2 rounded-full bg-black/55 px-2.5 py-1 text-[11px] font-medium text-white backdrop-blur-sm"
                aria-hidden="true"
              >
                {active + 1} / {total}
              </div>

              <div className="sr-only" aria-live="polite" aria-atomic="true">
                {`${active + 1} / ${total}`}
              </div>
            </>
          )}
        </div>
      </div>

      {total > 1 && (
        <div
          className="mt-3 flex justify-center gap-1.5 px-4"
          role="tablist"
          aria-label={
            t("tour.galleryLabel", { defaultValue: "Galería de imágenes del tour" }) as string
          }
        >
          {images.map((_, i) => (
            <button
              key={i}
              type="button"
              role="tab"
              aria-selected={i === active}
              aria-label={`${t("common.goToImage", { defaultValue: "Ir a la imagen" })} ${i + 1}`}
              onClick={() => scrollToIndex(i)}
              className={`h-2 rounded-full transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                i === active ? "w-6 bg-foreground" : "w-2 bg-foreground/30"
              }`}
            />
          ))}
        </div>
      )}
    </section>
  );
}
